import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaRegClock,
  FaArrowRight,
  FaArrowLeft,
  FaRegNewspaper,
} from "react-icons/fa";
import Navbar from "./Navbar";
import Footer from "./footer";
import { Button, Card, Container, IconTile, SectionHead } from "./ui";
import { fetchBlogs, formatBlogDate } from "../../services/blogs";

/** Grey card skeletons shown while the list request is in flight. */
const BlogSkeleton = () => (
  <Card className="overflow-hidden">
    <div className="h-44 animate-pulse bg-brand-50" />
    <div className="space-y-3 p-5">
      <div className="h-3 w-24 animate-pulse rounded bg-brand-50" />
      <div className="h-4 w-full animate-pulse rounded bg-brand-50" />
      <div className="h-4 w-2/3 animate-pulse rounded bg-brand-50" />
    </div>
  </Card>
);

const BlogCard = ({ blog }) => (
  <Card className="flex flex-col overflow-hidden hover:-translate-y-1 hover:shadow-lg">
    {/* Thumbnail */}
    <div className="relative h-44 overflow-hidden bg-brand-50">
      {blog.image ? (
        <img
          src={blog.image}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <FaRegNewspaper className="h-8 w-8 text-brand-300" />
        </div>
      )}
    </div>

    {/* Body */}
    <div className="flex flex-1 flex-col p-5">
      <h3 className="text-base font-bold leading-snug text-ink">
        {blog.title}
      </h3>

      {blog.excerpt && (
        <p className="mt-3 flex-1 text-sm leading-relaxed text-darkgrey line-clamp-3">
          {blog.excerpt}
        </p>
      )}

      {/* Meta row */}
      <div className="mt-5 flex items-center justify-between border-t border-brand-100 pt-4">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-darkgrey">
          <FaRegClock className="h-3.5 w-3.5 text-brand" />
          {formatBlogDate(blog.date) || "—"}
        </span>
        <Button as={Link} to={`/blogs/${blog.id}`} size="sm" variant="tint">
          Read More
          <FaArrowRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  </Card>
);

/** Prev / numbered pages / Next. Hidden entirely when there is one page. */
const Pagination = ({ page, pageCount, onChange }) => {
  if (pageCount <= 1) return null;

  return (
    <nav
      aria-label="Blog pages"
      className="mt-12 flex flex-wrap items-center justify-center gap-2"
    >
      <Button
        variant="outline"
        size="sm"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="disabled:cursor-not-allowed disabled:opacity-40"
      >
        <FaArrowLeft className="h-3 w-3" />
        Prev
      </Button>

      {Array.from({ length: pageCount }, (_, index) => index + 1).map(
        (pageNumber) => (
          <Button
            key={pageNumber}
            variant={pageNumber === page ? "solid" : "ghost"}
            size="sm"
            aria-current={pageNumber === page ? "page" : undefined}
            onClick={() => onChange(pageNumber)}
          >
            {pageNumber}
          </Button>
        )
      )}

      <Button
        variant="outline"
        size="sm"
        disabled={page >= pageCount}
        onClick={() => onChange(page + 1)}
        className="disabled:cursor-not-allowed disabled:opacity-40"
      >
        Next
        <FaArrowRight className="h-3 w-3" />
      </Button>
    </nav>
  );
};

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");

    fetchBlogs(page)
      .then(({ blogs: rows, pagination }) => {
        if (cancelled) return;
        setBlogs(rows);
        setPageCount(pagination.pageCount ?? 0);
        setStatus("ready");
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error fetching blogs:", error);
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [page]);

  const goToPage = (nextPage) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex flex-col">
      <Navbar />

      <section className="bg-white py-20">
        <Container>
          <SectionHead
            eyebrow="Blogs"
            title="Insights & updates"
            subtitle="News, guidance and stories from the SMART training team."
          />

          {status === "loading" && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((key) => (
                <BlogSkeleton key={key} />
              ))}
            </div>
          )}

          {status === "error" && (
            <Card className="flex flex-col items-center gap-4 p-12 text-center">
              <IconTile icon={FaRegNewspaper} size="lg" />
              <div>
                <h3 className="text-base font-bold text-ink">
                  Couldn't load the blogs
                </h3>
                <p className="mt-1 text-sm text-darkgrey">
                  The blog service didn't respond. Please try again shortly.
                </p>
              </div>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </Card>
          )}

          {status === "ready" && blogs.length === 0 && (
            <Card className="flex flex-col items-center gap-4 p-12 text-center">
              <IconTile icon={FaRegNewspaper} size="lg" />
              <div>
                <h3 className="text-base font-bold text-ink">No posts yet</h3>
                <p className="mt-1 text-sm text-darkgrey">
                  Check back soon for news and updates.
                </p>
              </div>
            </Card>
          )}

          {status === "ready" && blogs.length > 0 && (
            <>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {blogs.map((blog) => (
                  <BlogCard key={blog.id} blog={blog} />
                ))}
              </div>
              <Pagination
                page={page}
                pageCount={pageCount}
                onChange={goToPage}
              />
            </>
          )}
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default Blogs;
